import React, { FormEvent, useEffect, useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios, { apiBaseUrl } from '../api/httpClient';
import { useAuth } from '../auth/AuthContext';

const safeReturnTo = (value: string | null): string => {
  if (!value || !value.startsWith('/') || value.startsWith('//')) {
    return '/';
  }

  return value;
};

const LoginPage: React.FC = () => {
  const { user, login, completeMicrosoftLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMicrosoftSubmitting, setIsMicrosoftSubmitting] = useState(false);
  const [isMicrosoftAvailable, setIsMicrosoftAvailable] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';

  useEffect(() => {
    let isMounted = true;

    if (!apiBaseUrl) {
      return () => {
        isMounted = false;
      };
    }

    axios.get<{ enabled: boolean }>(`${apiBaseUrl}/auth/microsoft/status`)
      .then((response) => {
        if (isMounted) {
          setIsMicrosoftAvailable(response.data.enabled);
        }
      })
      .catch(() => {
        if (isMounted) {
          setIsMicrosoftAvailable(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.substring(1)
      : window.location.hash;

    if (!hash) {
      return;
    }

    const params = new URLSearchParams(hash);
    const microsoftToken = params.get('microsoft_token');
    const microsoftError = params.get('microsoft_error');
    const returnTo = safeReturnTo(params.get('return_to') ?? from);

    if (!microsoftToken && !microsoftError) {
      return;
    }

    window.history.replaceState(null, document.title, `${window.location.pathname}${window.location.search}`);

    if (microsoftError) {
      setError(microsoftError);
      return;
    }

    if (!microsoftToken) {
      return;
    }

    setError(null);
    setIsMicrosoftSubmitting(true);

    completeMicrosoftLogin(microsoftToken)
      .then(() => navigate(returnTo, { replace: true }))
      .catch(() => setError('Microsoft sign-in was accepted, but GCM could not start a session.'))
      .finally(() => setIsMicrosoftSubmitting(false));
  }, [completeMicrosoftLogin, from, navigate]);

  if (user && !isMicrosoftSubmitting) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch {
      setError('The email address or password was not accepted.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMicrosoftSignIn = () => {
    setError(null);

    if (!apiBaseUrl) {
      setError('GCM API URL is not configured.');
      return;
    }

    setIsMicrosoftSubmitting(true);
    window.location.assign(`${apiBaseUrl}/auth/microsoft/redirect?return_to=${encodeURIComponent(safeReturnTo(from))}`);
  };

  return (
    <Container className="login-page">
      <Card className="login-panel border-0 shadow-sm">
        <Card.Body>
          <p className="login-panel__eyebrow">Gemstone Operations</p>
          <h1>Sign in to GCM</h1>
          {error && <Alert variant="danger">{error}</Alert>}
          {isMicrosoftAvailable && (
            <>
              <Button
                className="login-panel__microsoft-button"
                disabled={isSubmitting || isMicrosoftSubmitting}
                onClick={handleMicrosoftSignIn}
                type="button"
                variant="outline-primary"
              >
                {isMicrosoftSubmitting && <Spinner animation="border" size="sm" className="me-2" />}
                Sign in with Microsoft
              </Button>
              <div className="login-panel__divider">
                <span>or use password</span>
              </div>
            </>
          )}
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="gcm-login-email">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                autoComplete="email"
                disabled={isSubmitting || isMicrosoftSubmitting}
                onChange={(event) => setEmail(event.target.value)}
                required
                type="email"
                value={email}
              />
            </Form.Group>
            <Form.Group controlId="gcm-login-password">
              <Form.Label>Password</Form.Label>
              <Form.Control
                autoComplete="current-password"
                disabled={isSubmitting || isMicrosoftSubmitting}
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />
            </Form.Group>
            <Button disabled={isSubmitting || isMicrosoftSubmitting} type="submit">
              {isSubmitting && <Spinner animation="border" size="sm" className="me-2" />}
              Sign in
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default LoginPage;
